import { useContext } from 'react'
import PropTypes from 'prop-types'
import swal from 'sweetalert2'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrashAlt } from '@fortawesome/pro-light-svg-icons'

import {
  Box,
  Button,
  Form,
  ModalContext,
  SectionDivider,
  SidebarModal,
  SidebarNoticeBox,
} from '@domain-group/suit-theme'

import { digObject, deepSetObject } from '@domain-group/javascript-utils'

import useProviderDeadlineGroup, { useProviderDeadlineGroupForm } from '@hooks/useProviderDeadlineGroup'
import useProviderDeadlineGroups from '@hooks/useProviderDeadlineGroups'
import useServices from '@hooks/useServices'

import ServiceItem from './components/ServiceItem'

const confirmDelete = (deleteProviderDeadlineGroup, deleteFn, closeModal) => {
  const deleteProviderDeadlineGroupPayload = {
    callbacks: {
      action: deleteFn,
      afterAction: closeModal,
    },
    toastText: 'Deadline Group Deleted',
  }

  swal.fire({
    confirmButtonColor: '#DD6B55',
    confirmButtonText: 'Yes, delete it.',
    icon: 'warning',
    showCancelButton: true,
    text: 'Are you sure?',
    title: 'Delete Deadline Group?',
  }).then(({ value }) => {
    if (value){
      deleteProviderDeadlineGroup(deleteProviderDeadlineGroupPayload)
    }
  })
}

const confirmSetAsDefault = (setAsDefault, setAsDefaultFn) => {
  const setAsDefaultPayload = {
    callbacks: {
      action: setAsDefaultFn,
    },
    toastText: 'Deadline Group Default Updated',
  }

  swal.fire({
    confirmButtonColor: '#43ac6a',
    confirmButtonText: 'Yes, update it.',
    icon: 'success',
    showCancelButton: true,
    text: 'Are you sure?',
    title: 'Set this Deadline Group to Default?',
  }).then(({ value }) => {
    if (value){
      setAsDefault(setAsDefaultPayload)
    }
  })
}

const updateDeadlinesParam = (deadlineTime, deadlines, serviceKey, setEntityState) => {
  const updatedDeadlines = deepSetObject(deadlines, `${serviceKey}.time`, deadlineTime)

  if (!deadlineTime) delete updatedDeadlines[serviceKey]

  setEntityState({ deadlines: updatedDeadlines })
}

const createOrUpdateProviderDeadlineGroup = (entityState, props, createFn, updateFn) => {
  const {
    callbacks: { closeModal, createProviderDeadlineGroup, updateProviderDeadlineGroup },
  } = props

  const text = entityState.id ? 'Updated' : 'Created'
  const actionFn = entityState.id ? updateFn : createFn

  const providerDeadlineGroupPayload = {
    callbacks: {
      action: actionFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: `Deadline Group ${text}`,
  }

  if (entityState.id) return updateProviderDeadlineGroup(providerDeadlineGroupPayload)

  return createProviderDeadlineGroup(providerDeadlineGroupPayload)
}

const CreateOrEditProviderDeadlineGroupModal = (props) => {
  const { callbacks, modalKey, showModal } = props
  const { closeModal, deleteProviderDeadlineGroup, setAsDefault } = callbacks

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, modalKey, {})
  const { providerDeadlineGroup: initProviderDeadlineGroup } = modalPayload

  const { filteredServices, loading: loadingServices } = useServices()

  const {
    callbacks: {
      createProviderDeadlineGroup: createFn,
      deleteProviderDeadlineGroup: deleteFn,
      setAsDefault: setAsDefaultFn,
      updateProviderDeadlineGroup: updateFn,
    },
    creating,
    deleting,
    providerDeadlineGroup,
    updating,
  } = useProviderDeadlineGroup(initProviderDeadlineGroup)

  const {
    entityState,
    entityState: {
      deadlines,
      default: deadlineGroupDefault,
      id,
      title,
    },
    errors,
    handlers,
    saveEnabled,
    setEntityState,
  } = useProviderDeadlineGroupForm(providerDeadlineGroup, { validateOn: 'change' })

  const providerId = providerDeadlineGroup?.provider_id

  const { hasFilteredProviderDeadlineGroups } = useProviderDeadlineGroups({
    filters: { provider_id: providerId },
    performHttpRequests: !!providerId,
  })

  const saveOrCreateButtonText = id ? 'Save Changes' : 'Add Deadline Group'

  return (
    <SidebarModal
      callbacks={callbacks}
      modalKey={modalKey}
      showModal={showModal}
      size="small"
    >
      <SidebarModal.Header
        callbacks={callbacks}
        title={id ? 'Edit' : 'Add'}
        titleSecondLine="Deadline Group"
      />

      <SidebarModal.Content>
        <Box flexDirection="column" flexShrink={0}>

          <Form.Field errorMessage={errors.title} label="* Title">
            <input name="title" value={title} type="text" {...handlers} />
          </Form.Field>

          {!hasFilteredProviderDeadlineGroups && (
            <Form.Field marginTop="large" label="Default">
              <select
                name="default"
                value={deadlineGroupDefault}
                {...handlers}
              >
                <option value="false">False</option>
                <option value="true">True</option>
              </select>
            </Form.Field>
          )}

          {id && (
            <Button
              buttonStyle="ghostEdit"
              disabled={deadlineGroupDefault}
              marginTop="large"
              onClick={() => confirmSetAsDefault(setAsDefault, setAsDefaultFn)}
              size="medium"
              width="auto"
            >
              {deadlineGroupDefault ? 'Default for Provider' : 'Set as Default'}
            </Button>
          )}

          <SectionDivider boxProps={{ marginY: 'large' }}>DEADLINES</SectionDivider>

          <SidebarNoticeBox boxProps={{ marginBottom: 0 }}>
            <SidebarNoticeBox.Title>
              Please provide Deadlines in 24 hour time
            </SidebarNoticeBox.Title>

            <SidebarNoticeBox.Title>
              e.g. 14:00
            </SidebarNoticeBox.Title>
          </SidebarNoticeBox>

          {!loadingServices && (
            filteredServices.map(service => (
              <ServiceItem
                callbacks={{
                  updateDeadlinesParam: (deadlineTime, serviceKey) => (
                    updateDeadlinesParam(deadlineTime, deadlines, serviceKey, setEntityState)
                  ),
                }}
                deadlines={deadlines}
                key={service.id}
                service={service}
              />
            ))
          )}

          {id && (
            <Form.Field direction="column" label="More Options" boxProps={{ marginTop: 20 }}>
              <Button
                buttonStyle="secondaryUtility"
                icon={<FontAwesomeIcon icon={faTrashAlt} />}
                loading={deleting}
                onClick={() => confirmDelete(deleteProviderDeadlineGroup, deleteFn, closeModal)}
                size="medium"
              >
                Delete Deadline Group
              </Button>
            </Form.Field>
          )}
        </Box>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          onClick={() => createOrUpdateProviderDeadlineGroup(entityState, props, createFn, updateFn)}
          size="large"
        >
          {saveEnabled ? saveOrCreateButtonText : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

CreateOrEditProviderDeadlineGroupModal.propTypes = {
  callbacks: PropTypes.object.isRequired,
  modalKey: PropTypes.string,
  showModal: PropTypes.bool,
}

CreateOrEditProviderDeadlineGroupModal.defaultProps = {
  modalKey: 'CreateOrEditProviderDeadlineGroupModal',
}

const LazyLoadedModal = props => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditProviderDeadlineGroupModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
